/**
 * Cross-sections as a DXF, written in the browser.
 *
 * Three layouts, any combination of which can go into one file:
 *
 *  - **sheet** — each station a 2D figure in (offset, elevation), tiled on a
 *    grid. The classic cross-section sheet.
 *  - **plan**  — a printable top-down drawing: centreline, corridor edges,
 *    a tick and chainage label per station, and one quad per bin coloured by
 *    net cut/fill. Flattened to `z = 0` so it plots at a scale.
 *  - **model** — the same section polylines at their true position and
 *    heading along the alignment, in project coordinates, 1:1:1.
 *
 * The geometry comes from `sectionModel.ts` — `bandPolygons`, `validRuns` —
 * called directly rather than reimplemented, so the DXF is provably the same
 * geometry the Section tab draws, daylight crossings and data holes included.
 *
 * This is client-side on purpose. DXF needs no converter (DWG would), and the
 * browser already holds everything: the bins JSON, the decoded section planes,
 * and the alignment itself — the client is the *owner* of `core/alignment.ts`,
 * so it places georeferenced geometry more exactly than the worker can, which
 * only ever sees a tessellated snapshot.
 */
import { pointAtStation, type Alignment } from '@/core/alignment';

import { type DxfLayer, type DxfPoint, type DxfTextAlign, dxfPolyline, dxfText } from './dxf';
import { staOnly } from './format';
import { bandPolygons, offsetCentre, sectionSpansAcross, validRuns, VE_PRESETS } from './sectionModel';
import { activePairIndices, type SeriesRole, type SeriesRows } from './sectionSeries';
import type { StationBin, StationSectionLine } from './types';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** One extra drawn plane — a stack level, a design line, an overlay. */
export interface SectionSeriesInput {
  /** Human name, as the chart's legend says it. Sanitised into a layer name. */
  name: string;
  role: SeriesRole;
  z: Float32Array;
}

export interface SectionInput {
  /** Internal station — where the section plane cuts. Model/plan modes need it. */
  staInternalM: number;
  /** Display station, for the label. */
  staDisplayM: number;
  zTop: Float32Array;
  zBottom: Float32Array;
  cutAreaM2: number;
  fillAreaM2: number;
  /** Every OTHER drawn plane — the rest of a stack, its design lines, the
   *  overlays. Each goes on its own `SECTION-<NAME>` layer. */
  series?: readonly SectionSeriesInput[];
}

export interface SheetOptions {
  offsetStartM: number;
  offsetStepM: number;
  /** Vertical exaggeration. Stated on every cell — an unlabelled one is a lie. */
  ve: number;
  columns: number;
  /** Gap between cells, in metres of drawing space. */
  gapX: number;
  gapY: number;
  textHeight: number;
  /** Extra title-block line, e.g. that these are interval means, not true cuts. */
  note?: string;
}

export interface PlanOptions {
  swathLeftM: number;
  swathRightM: number;
  textHeight: number;
  /** Label every Nth station line, so a 20 m interval over 5 km stays readable. */
  labelEvery?: number;
}

export interface ModelOptions {
  offsetStartM: number;
  offsetStepM: number;
  /** Station-label height, in metres of model space. Defaults to 1 m. */
  textHeight?: number;
}

/** Owner cap on one file. Mirrors `MAX_STATION_PAGES` in `perStationPdf.ts` —
 *  one number the user has already met beats a second one. */
export const MAX_DXF_SECTIONS = 400;

export const SHEET_LAYERS: DxfLayer[] = [
  { name: 'EXISTING', color: 3 },
  { name: 'DESIGN', color: 5 },
  { name: 'CUT', color: 1 },
  { name: 'FILL', color: 4 },
  { name: 'GRID', color: 8 },
  // The readability mesh, apart from the frame and its ticks so a reader who
  // finds it busy can freeze it without losing the frame the section sits in.
  { name: 'GRID-FINE', color: 9 },
  { name: 'TEXT', color: 7 },
];

export const PLAN_LAYERS: DxfLayer[] = [
  { name: 'ALIGNMENT', color: 7 },
  { name: 'CORRIDOR', color: 8 },
  { name: 'STATION-TICK', color: 8 },
  { name: 'BIN-CUT', color: 1 },
  { name: 'BIN-FILL', color: 4 },
  { name: 'TEXT', color: 7 },
];

export const MODEL_LAYERS: DxfLayer[] = [
  { name: 'SECTION-EXISTING', color: 3 },
  { name: 'SECTION-DESIGN', color: 5 },
  { name: 'ALIGNMENT', color: 7 },
  { name: 'TEXT', color: 7 },
];

// ---------------------------------------------------------------------------
// Series layers — one per extra drawn plane
// ---------------------------------------------------------------------------

const LAYER_PREFIX = 'SECTION-';
/** R12's layer-name limit, and the length `dxf.ts::layerName` truncates at. */
const MAX_LAYER_NAME = 31;

/**
 * DXF layer-name rules: upper-case, `[A-Z0-9_-]`, <= 31 chars, unique.
 *
 * `taken` carries the names already handed out, so two levels that sanitise to
 * the same string get `-2`, `-3`, ... rather than silently sharing a layer.
 * Passing the SAME set through one document is what makes the name a section
 * draws on identical in every cell.
 *
 * The disambiguator is fitted INSIDE the limit rather than appended past it:
 * `dxf.ts::layerName` truncates at 31 on the way out, so a 32-character
 * candidate loses its `-2` and lands back on the layer it was disambiguating
 * from — two series silently sharing one layer, which is the exact failure
 * this function exists to prevent. Uniqueness is therefore guaranteed here,
 * never delegated downstream.
 */
export function seriesLayerName(name: string, taken: Set<string>): string {
  const core =
    name
      .normalize('NFKD')
      // eslint-disable-next-line no-control-regex
      .replace(/[^\x00-\x7F]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      // 22 keeps the undisambiguated name (8 + 22 = 30) inside the limit.
      .slice(0, 22) || 'SERIES';
  let candidate = `${LAYER_PREFIX}${core}`;
  for (let n = 2; taken.has(candidate); n++) {
    const suffix = `-${n}`;
    // Whatever the suffix costs comes out of the core, not out of the limit.
    const room = Math.max(1, MAX_LAYER_NAME - LAYER_PREFIX.length - suffix.length);
    candidate = `${LAYER_PREFIX}${core.slice(0, room)}${suffix}`;
  }
  taken.add(candidate);
  return candidate;
}

const SERIES_COLOR: Record<SeriesRole, number> = { asbuilt: 2, design: 5, reference: 6 };

/** name -> role, first-seen order, across every section. */
function seriesNames(sections: readonly SectionInput[]): Map<string, SeriesRole> {
  const m = new Map<string, SeriesRole>();
  for (const s of sections) for (const x of s.series ?? []) if (!m.has(x.name)) m.set(x.name, x.role);
  return m;
}

/**
 * name -> layer name, resolved ONCE over every section so a name is stable
 * from cell to cell: a level that only appears at station 3 must not take the
 * layer a level at station 1 already earned, and the same level must not land
 * on `SECTION-FORMATION` in one cell and `SECTION-FORMATION-2` in the next.
 */
function seriesLayerMap(sections: readonly SectionInput[]): Map<string, string> {
  const taken = new Set<string>();
  const out = new Map<string, string>();
  for (const name of seriesNames(sections).keys()) out.set(name, seriesLayerName(name, taken));
  return out;
}

/**
 * The extra layers one document needs, in first-seen order.
 *
 * The writers resolve the same map, so TABLES declares exactly the layers
 * ENTITIES draws on — a DXF referencing an undeclared layer is a file some
 * readers repair and others reject.
 */
export function sectionSeriesLayers(sections: readonly SectionInput[]): DxfLayer[] {
  const layers = seriesLayerMap(sections);
  const out: DxfLayer[] = [];
  for (const [name, role] of seriesNames(sections)) out.push({ name: layers.get(name)!, color: SERIES_COLOR[role] });
  return out;
}

/**
 * One station line's `SectionInput` planes, split into the active PAIR and the
 * extras — the composition the dock's `buildDxfSections` needs, here rather
 * than there so it is testable without a rendered dock.
 *
 * The pair is whichever two as-built levels the stack chip selected: the
 * cut/fill bands, the printed areas and the `EXISTING`/`DESIGN` layers all
 * belong to it. Everything else the section draws — the rest of the stack, its
 * design lines, the overlays — comes back as `series` and lands on its own
 * layer, so an export carries the whole stack rather than the two levels that
 * happen to be selected.
 */
export function dxfSeriesForLine(
  rows: SeriesRows,
  planePair: readonly [string, string] | null,
  designLabel: string,
): { zTop: Float32Array; zBottom: Float32Array; series: SectionSeriesInput[] } {
  const [ia, ib] = activePairIndices(rows, planePair);
  return {
    zTop: rows.asbuilt[ia],
    zBottom: rows.asbuilt[ib],
    series: [
      ...rows.asbuilt.flatMap((z, i) =>
        i === ia || i === ib ? [] : [{ name: rows.asbuiltLabels[i], role: 'asbuilt' as const, z }],
      ),
      // Design plane `i` is level `i`'s design line; the index fallback keeps a
      // file with more design planes than as-built levels naming them at all.
      ...rows.design.map((z, i) => ({
        name: `${designLabel} · ${rows.asbuiltLabels[i] ?? i + 1}`,
        role: 'design' as const,
        z,
      })),
      ...rows.reference.map((r) => ({ name: r.series.label, role: 'reference' as const, z: r.row })),
    ],
  };
}

/** Every plane a section draws, for the shared vertical scale. */
const allPlanes = (s: SectionInput): Float32Array[] => [s.zTop, s.zBottom, ...(s.series ?? []).map((x) => x.z)];

// ---------------------------------------------------------------------------
// Shared geometry
// ---------------------------------------------------------------------------

/**
 * The point at `offset` metres across the alignment at internal station `s`.
 *
 * `cross(tangent, p − a) > 0` is LEFT and a NEGATIVE offset
 * (`core/alignment.ts` `projectPoint`), so the left normal is `(-ty, tx)` and
 * the RIGHT normal — the direction a positive offset runs in — is `(ty, -tx)`.
 * Same convention and same derivation as
 * `scripts/station_bins.py::sample_station_sections`, which is what makes the
 * drawn section land where the worker measured it.
 */
export function pointAcross(a: Alignment, s: number, offset: number, z: number): DxfPoint {
  const p = pointAtStation(a, s);
  return { x: p.x + p.ty * offset, y: p.y - p.tx * offset, z };
}

/**
 * The elevation range every drawn section shares, so cells are comparable.
 *
 * `valid` is false when nothing was measured, or when every section is
 * perfectly flat — the placeholder `[0, 1]` span keeps the drawing arithmetic
 * finite, but a caller choosing a *scale* from it would be scaling to a number
 * that came from nowhere.
 */
function documentSpan(sections: readonly SectionInput[]): { lo: number; hi: number; valid: boolean } {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of sections) {
    const span = sectionSpansAcross(allPlanes(s));
    if (!span.valid) continue;
    if (span.lo < lo) lo = span.lo;
    if (span.hi > hi) hi = span.hi;
  }
  return Number.isFinite(lo) && hi > lo ? { lo, hi, valid: true } : { lo: 0, hi: 1, valid: false };
}

/**
 * A sheet exaggeration that makes the drawn section about a third as tall as
 * the corridor is wide — the aspect ratio a cross-section reads well at on
 * paper. Snapped to the offered presets, and chosen multiplicatively (5x and
 * 20x are equally far from 10x), the same way `autoExaggeration` picks one for
 * the on-screen pane.
 *
 * A default, never a silent one: the sheet states the exaggeration on every
 * cell, and the dialog lets the user override it.
 */
export function defaultSheetVe(sections: readonly SectionInput[], swathWidthM: number): number {
  // The tallest SECTION, not the whole run's elevation range: the cell has to
  // fit one section, and scaling to the run leaves every cell mostly empty.
  const zSpan = tallestSection(sections);
  if (!(zSpan > 0) || !(swathWidthM > 0)) return 2;
  const wanted = (swathWidthM / 3) / zSpan;
  let best: number = VE_PRESETS[0];
  let bestErr = Infinity;
  for (const p of VE_PRESETS) {
    const err = Math.abs(Math.log(p / wanted));
    if (err < bestErr) {
      bestErr = err;
      best = p;
    }
  }
  return best;
}

/**
 * The tallest single section, which is what a CELL has to fit.
 *
 * Not the document span: over a kilometre of road that climbs 10 m, the
 * document span is 10 m while no one section spans more than about one. A
 * sheet scaled to the document span draws every cell 90 % empty, with the
 * profile riding high or low in its frame according to the chainage's
 * elevation rather than to anything about the section.
 */
function tallestSection(sections: readonly SectionInput[]): number {
  let tallest = 0;
  for (const s of sections) {
    const span = sectionSpansAcross(allPlanes(s));
    if (span.valid && span.hi - span.lo > tallest) tallest = span.hi - span.lo;
  }
  return tallest;
}

/** A round elevation for a cell's datum, so the printed number reads cleanly. */
function datumStep(windowM: number): number {
  const raw = windowM / 4;
  for (const c of [0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 50]) if (raw <= c) return c;
  return 100;
}

/** A round elevation interval, for roughly six gridlines up a cell. */
function elevationStep(windowM: number): number {
  const raw = windowM / 6;
  for (const c of [0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 50]) if (raw <= c) return c;
  return 100;
}

/**
 * The existing surface's elevation ON the centreline — what a cross-section is
 * read against on site, and so the zero of the sheet's elevation axis.
 *
 * The NEAREST measured column, not the centre column: a crown, a kerb line or
 * a survey gap puts a hole exactly there often enough that reading only the
 * centre column would drop the axis on a section that plainly has a
 * centreline. `null` when the section measured no existing surface at all, in
 * which case the axis falls back to absolute elevations — there is nothing to
 * be relative to, and inventing a zero would be a claim about a surface that
 * was never sampled.
 */
function centrelineElevation(values: Float32Array, offsetStartM: number, offsetStepM: number): number | null {
  let best: number | null = null;
  let bestD = Infinity;
  for (let j = 0; j < values.length; j++) {
    if (!Number.isFinite(values[j])) continue;
    const d = Math.abs(offsetCentre(offsetStartM, offsetStepM, j));
    if (d < bestD) {
      bestD = d;
      best = values[j];
    }
  }
  return best;
}

/** A round tick interval that yields roughly 8 ticks across the span. */
function tickStep(span: number): number {
  const raw = span / 8;
  for (const c of [0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 500]) if (raw <= c) return c;
  return 1000;
}

const fmtArea = (v: number): string => v.toFixed(2);

// ---------------------------------------------------------------------------
// Sheet
// ---------------------------------------------------------------------------

/**
 * Sections tiled left-to-right then top-to-bottom, one drawing unit per metre.
 *
 * Every cell shares ONE vertical scale and ONE exaggeration, both derived from
 * the whole set: a sheet whose cells each had their own scale would invite
 * exactly the comparison it cannot support. The same reasoning governs
 * `documentExaggeration` in the per-station PDF.
 */
export function sectionSheetEntities(sections: readonly SectionInput[], o: SheetOptions): string[] {
  const out: string[] = [];
  if (sections.length === 0) return out;

  // Every drawn plane, not just the active pair: a cell whose frame was sized
  // to the pair would clip an overlay sampled over a wider grid.
  const nOff = Math.max(...sections.map((s) => Math.max(...allPlanes(s).map((p) => p.length))), 1);
  const seriesLayers = seriesLayerMap(sections);
  const cellW = nOff * o.offsetStepM;
  const doc = documentSpan(sections);
  const th = o.textHeight;
  // A cell is sized to the TALLEST section plus a margin above and below, and
  // each section is then centred in its own cell against its own datum. One
  // shared datum, which is what this used to do, means the frame has to span
  // the whole run's elevation range: on a road that climbs, every profile is
  // then a flat squiggle riding high or low in a mostly empty frame according
  // to its chainage. The SCALE stays shared — that is what makes two cells
  // comparable — and the datum each cell was drawn against is printed on it,
  // which is what a cross-section sheet has always done.
  const padV = th * 1.5;
  const cellH = Math.max(tallestSection(sections) * o.ve + padV * 2, th * 4);
  const windowM = cellH / o.ve; // elevation the frame spans, at this VE
  const dStep = datumStep(windowM);
  const cols = Math.max(1, Math.floor(o.columns));
  const oStep = tickStep(cellW);

  // A gutter has to clear what is drawn IN it, and what is drawn in it is
  // text: the title block rises 3.9·th above a cell and the offset labels drop
  // 2.6·th below one, so rows closer than 6.5·th collide however generous the
  // caller's gap looked in metres. The caller's value is a preference; these
  // are floors, because a gap that reads well at a 30 m swath is a pile-up at
  // 5 m.
  const gapY = Math.max(o.gapY, th * 8);
  // The elevation axis hangs its labels off the LEFT edge of a cell, so the
  // gutter has to clear a right-aligned "-12.50" as well as the cells either
  // side of it — 4·th was sized when nothing was drawn out there.
  const gapX = Math.max(o.gapX, th * 9);

  sections.forEach((s, i) => {
    const ox = (i % cols) * (cellW + gapX);
    const oy = -Math.floor(i / cols) * (cellH + gapY);
    const x0 = ox + o.offsetStartM;

    // This cell's datum: the elevation its frame's bottom edge stands for.
    // Centre the section in the frame, snap to a round value so the printed
    // number is readable, then clamp so snapping can never push the profile
    // through the frame it was centred in.
    const own = sectionSpansAcross(allPlanes(s));
    const lo = own.valid ? own.lo : doc.lo;
    const hi = own.valid ? own.hi : doc.hi;
    const centred = lo - (windowM - (hi - lo)) / 2;
    const datum = Math.min(
      Math.max(Math.round(centred / dStep) * dStep, hi - windowM + padV / o.ve),
      lo - padV / o.ve,
    );
    const place = (offset: number, z: number): DxfPoint => ({
      x: ox + offset,
      y: oy + (z - datum) * o.ve,
      z: 0,
    });

    // --- the two surfaces, broken at every data hole -----------------------
    for (const [values, layer] of [
      [s.zBottom, 'EXISTING'],
      [s.zTop, 'DESIGN'],
    ] as const) {
      for (const run of validRuns(values)) {
        const pts: DxfPoint[] = [];
        for (let j = run.from; j <= run.to; j++) {
          pts.push(place(offsetCentre(o.offsetStartM, o.offsetStepM, j), values[j]));
        }
        out.push(dxfPolyline(pts, { layer }));
      }
    }

    // --- cut / fill bands, as closed boundary polylines (no hatch) ---------
    for (const band of bandPolygons(s.zTop, s.zBottom, o.offsetStartM, o.offsetStepM)) {
      out.push(
        dxfPolyline(
          band.points.map((p) => place(p.o, p.z)),
          { layer: band.kind === 'cut' ? 'CUT' : 'FILL', closed: true },
        ),
      );
    }

    // --- every other drawn plane, on its own layer -------------------------
    // Lines only: a band is a claim about a PAIR, and the sheet's cut/fill is
    // already the active pair's. An extra level draws its profile and nothing
    // more.
    for (const x of s.series ?? []) {
      const layer = seriesLayers.get(x.name);
      if (!layer) continue;
      for (const run of validRuns(x.z)) {
        const pts: DxfPoint[] = [];
        for (let j = run.from; j <= run.to; j++) pts.push(place(offsetCentre(o.offsetStartM, o.offsetStepM, j), x.z[j]));
        out.push(dxfPolyline(pts, { layer }));
      }
    }

    // --- frame, offset ticks, title block ----------------------------------
    out.push(
      dxfPolyline(
        [
          { x: x0, y: oy, z: 0 },
          { x: x0 + cellW, y: oy, z: 0 },
          { x: x0 + cellW, y: oy + cellH, z: 0 },
          { x: x0, y: oy + cellH, z: 0 },
        ],
        { layer: 'GRID', closed: true },
      ),
    );
    const first = Math.ceil(o.offsetStartM / oStep) * oStep;
    for (let t = first; t <= o.offsetStartM + cellW + 1e-9; t += oStep) {
      const x = ox + t;
      out.push(dxfPolyline([{ x, y: oy, z: 0 }, { x, y: oy + o.textHeight, z: 0 }], { layer: 'GRID' }));
      // The mesh line the tick belongs to, full height of the cell.
      out.push(dxfPolyline([{ x, y: oy, z: 0 }, { x, y: oy + cellH, z: 0 }], { layer: 'GRID-FINE' }));
      const side = Math.abs(t) < 1e-6 ? 'CL' : `${Math.abs(Number(t.toFixed(3)))}${t < 0 ? 'L' : 'R'}`;
      // Centred on the tick it names: left-aligned, "15L" hangs a full label
      // width to the right of the line it belongs to.
      out.push(dxfText(x, oy - th * 1.8, th, side, 'TEXT', 'center'));
    }

    // --- elevation axis up the left edge, zeroed on the centreline ---------
    // Read against the centreline's own existing-surface elevation, which is
    // the height a section is discussed at, rather than against sea level:
    // "0.4 below the centreline" is the sentence, and an axis of absolute
    // metres makes the reader do that subtraction on every line. The absolute
    // datum stays printed under the cell, so the two remain convertible.
    const zCl = centrelineElevation(s.zBottom, o.offsetStartM, o.offsetStepM);
    const eStep = elevationStep(windowM);
    // Anchored so that a tick lands exactly on the zero, whatever it is.
    const eBase = zCl ?? 0;
    const eFirst = eBase + Math.ceil((datum - eBase) / eStep - 1e-9) * eStep;
    for (let z = eFirst; z <= datum + windowM + 1e-9; z += eStep) {
      const y = oy + (z - datum) * o.ve;
      out.push(dxfPolyline([{ x: x0, y, z: 0 }, { x: x0 + cellW, y, z: 0 }], { layer: 'GRID-FINE' }));
      // The stub sits OUTSIDE the frame: inside it would be one more line
      // crossing the profile at the exact height the reader is measuring.
      out.push(dxfPolyline([{ x: x0 - th * 0.6, y, z: 0 }, { x: x0, y, z: 0 }], { layer: 'GRID' }));
      const rel = zCl == null ? z : z - zCl;
      // `+` only on the positive side: a leading sign on every label reads as
      // noise, and its absence on 0.00 is what marks the zero.
      const label = `${rel > 1e-9 ? '+' : ''}${rel.toFixed(2)}`;
      // Dropped by a third of a text height so the label is centred ON the
      // gridline rather than sitting on top of it.
      out.push(dxfText(x0 - th * 0.8, y - th * 0.35, th, label, 'TEXT', 'right'));
    }

    out.push(dxfText(x0, oy + cellH + th * 2.6, th * 1.3, `STA ${staOnly(s.staDisplayM, 2)}`, 'TEXT'));
    out.push(
      dxfText(x0, oy + cellH + th * 1.1, th, `Cut ${fmtArea(s.cutAreaM2)} m2  Fill ${fmtArea(s.fillAreaM2)} m2`, 'TEXT'),
    );
    // Anchored to the cell's RIGHT edge. Left-aligned there it started in the
    // gutter and ended inside the next cell's title.
    out.push(dxfText(x0 + cellW, oy + cellH + th * 1.1, th, `VE ${o.ve}x`, 'TEXT', 'right'));
    // Each cell has its own datum, so each cell states it. Without this the
    // sheet would invite a comparison between two frames it cannot support.
    out.push(dxfText(x0, oy - th * 3.4, th * 0.9, `DATUM ${datum.toFixed(2)}`, 'TEXT'));
    // The absolute elevation the axis calls zero — the one number that turns
    // every relative height on this cell back into a level.
    if (zCl != null) {
      out.push(dxfText(x0 + cellW, oy - th * 3.4, th * 0.9, `CL ${zCl.toFixed(2)}`, 'TEXT', 'right'));
    }
  });

  // The note is a statement about the RUN, not about station 0+010, so it is
  // drawn once above the sheet rather than under every cell. Repeated per cell
  // it was also the sheet's worst collision: a sentence is wider than a cell,
  // so each copy ran through its neighbours' copies.
  if (o.note) out.push(dxfText(o.offsetStartM, cellH + th * 5.2, th, o.note, 'TEXT'));

  return out;
}

// ---------------------------------------------------------------------------
// Plan (top-down, printable)
// ---------------------------------------------------------------------------

/**
 * A printable top-down drawing of the corridor, flattened to `z = 0` so it
 * plots at a scale.
 *
 * The bin quads are the corridor edge points at each bin's start and end
 * station — the same four corners `station_bins.py::_corridor_ring` builds
 * server-side for `station_bins.geojson`, derived here from the alignment the
 * client already owns rather than round-tripping through a byproduct.
 */
export function sectionPlanEntities(
  bins: readonly StationBin[],
  stations: readonly StationSectionLine[] | null,
  alignment: Alignment,
  o: PlanOptions,
): string[] {
  const out: string[] = [];
  if (bins.length === 0) return out;

  const left = -Math.abs(o.swathLeftM);
  const right = Math.abs(o.swathRightM);

  // Station lines: the true ones when the run has them, otherwise the bin
  // edges, which are the same stations by construction.
  const lines =
    stations && stations.length > 0
      ? stations.map((s) => ({ sta: s.staInternalM, display: s.staDisplayM }))
      : [
          ...bins.map((b) => ({ sta: b.staStartInternalM, display: b.staStartDisplayM })),
          {
            sta: bins[bins.length - 1].staEndInternalM,
            display: bins[bins.length - 1].staEndDisplayM,
          },
        ];

  // --- centreline, as tessellated ------------------------------------------
  // `points` is a flat [x,y,z, x,y,z, …]; flattened to z = 0 for a plan sheet.
  const centre: DxfPoint[] = [];
  for (let i = 0; i + 2 < alignment.points.length; i += 3) {
    centre.push({ x: alignment.points[i], y: alignment.points[i + 1], z: 0 });
  }
  out.push(dxfPolyline(centre, { layer: 'ALIGNMENT' }));

  // --- corridor edges, sampled at the station lines -------------------------
  out.push(dxfPolyline(lines.map((l) => pointAcross(alignment, l.sta, left, 0)), { layer: 'CORRIDOR' }));
  out.push(dxfPolyline(lines.map((l) => pointAcross(alignment, l.sta, right, 0)), { layer: 'CORRIDOR' }));

  // --- one tick + chainage label per station line ---------------------------
  const every = Math.max(1, Math.floor(o.labelEvery ?? 1));
  lines.forEach((l, i) => {
    out.push(
      dxfPolyline([pointAcross(alignment, l.sta, left, 0), pointAcross(alignment, l.sta, right, 0)], {
        layer: 'STATION-TICK',
      }),
    );
    if (i % every !== 0 && i !== lines.length - 1) return;
    const at = pointAcross(alignment, l.sta, right + o.textHeight * 1.5, 0);
    out.push(dxfText(at.x, at.y, o.textHeight, staOnly(l.display, 2), 'TEXT'));
  });

  // --- one quad per bin, cut or fill by its net -----------------------------
  for (const b of bins) {
    // A bin that measured nothing gets no quad: an empty quad drawn in a
    // cut/fill colour is a claim the analysis never made.
    if (b.cellCount === 0) continue;
    const quad = [
      pointAcross(alignment, b.staStartInternalM, left, 0),
      pointAcross(alignment, b.staStartInternalM, right, 0),
      pointAcross(alignment, b.staEndInternalM, right, 0),
      pointAcross(alignment, b.staEndInternalM, left, 0),
    ];
    out.push(dxfPolyline(quad, { layer: b.netVolumeM3 < 0 ? 'BIN-CUT' : 'BIN-FILL', closed: true }));
    const mid = (b.staStartInternalM + b.staEndInternalM) / 2;
    const c = pointAcross(alignment, mid, 0, 0);
    out.push(dxfText(c.x, c.y, o.textHeight * 0.9, `${b.netVolumeM3.toFixed(1)} m3`, 'TEXT'));
  }

  return out;
}

// ---------------------------------------------------------------------------
// Model (georeferenced 3D)
// ---------------------------------------------------------------------------

/** Highest sample in a section, or null when it has none. */
function crownElevation(s: SectionInput): number | null {
  let hi = -Infinity;
  for (const values of [s.zTop, s.zBottom]) {
    for (const v of values) if (Number.isFinite(v) && v > hi) hi = v;
  }
  return hi === -Infinity ? null : hi;
}

/**
 * The station label for one section, at the section's own position, elevation
 * and heading.
 *
 * A stack of bare section lines in model space says nothing about which one is
 * 1+240 — the sheet layout has labelled every cell from the start, and this is
 * the same information where the geometry actually lives.
 *
 * Placement: off the low-offset end of the swath, so the text never crosses
 * the section it names, at the section's highest sample so it clears its own
 * geometry. Rotated to the section's heading, because in a corridor every
 * section points a different way and horizontal labels collide with each other
 * on any curve. A heading past ±90° would read upside down, so it is flipped
 * and the anchor swapped with it — the text then runs the other way from the
 * same point and still ends up outside the swath.
 */
function stationLabel(s: SectionInput, alignment: Alignment, o: ModelOptions): string {
  const z = crownElevation(s);
  if (z === null) return '';
  const th = o.textHeight ?? 1;
  const at = pointAcross(alignment, s.staInternalM, o.offsetStartM - th * 0.5, z);
  const edge = pointAcross(alignment, s.staInternalM, o.offsetStartM, z);
  // Heading of the section, i.e. the direction a positive offset runs in.
  const deg = (Math.atan2(at.y - edge.y, at.x - edge.x) * 180) / Math.PI + 180;
  let rotation = ((deg % 360) + 360) % 360;
  let align: DxfTextAlign = 'right';
  if (rotation > 90 && rotation <= 270) {
    rotation = ((rotation - 180) % 360 + 360) % 360;
    align = 'left';
  }
  return dxfText(at.x, at.y, th, `STA ${staOnly(s.staDisplayM, 2)}`, 'TEXT', align, { z, rotation });
}

/**
 * Sections at their true position and heading, in project coordinates, 1:1:1.
 * No exaggeration — this drops into model space beside the surfaces it was cut
 * from.
 */
export function sectionModelEntities(
  sections: readonly SectionInput[],
  alignment: Alignment,
  o: ModelOptions,
): string[] {
  const out: string[] = [];
  const seriesLayers = seriesLayerMap(sections);
  for (const s of sections) {
    for (const [values, layer] of [
      [s.zBottom, 'SECTION-EXISTING'],
      [s.zTop, 'SECTION-DESIGN'],
    ] as const) {
      for (const run of validRuns(values)) {
        const pts: DxfPoint[] = [];
        for (let j = run.from; j <= run.to; j++) {
          pts.push(pointAcross(alignment, s.staInternalM, offsetCentre(o.offsetStartM, o.offsetStepM, j), values[j]));
        }
        out.push(dxfPolyline(pts, { layer }));
      }
    }
    for (const x of s.series ?? []) {
      const layer = seriesLayers.get(x.name);
      if (!layer) continue;
      for (const run of validRuns(x.z)) {
        const pts: DxfPoint[] = [];
        for (let j = run.from; j <= run.to; j++)
          pts.push(pointAcross(alignment, s.staInternalM, offsetCentre(o.offsetStartM, o.offsetStepM, j), x.z[j]));
        out.push(dxfPolyline(pts, { layer }));
      }
    }
    out.push(stationLabel(s, alignment, o));
  }
  // The centreline at its real elevations — this drawing is 1:1:1.
  const centre: DxfPoint[] = [];
  for (let i = 0; i + 2 < alignment.points.length; i += 3) {
    centre.push({ x: alignment.points[i], y: alignment.points[i + 1], z: alignment.points[i + 2] });
  }
  out.push(dxfPolyline(centre, { layer: 'ALIGNMENT' }));
  return out;
}

// ---------------------------------------------------------------------------
// Scope + filename
// ---------------------------------------------------------------------------

/**
 * Which station lines a request covers. The dock's brush decides the range —
 * the current selection if there is one, otherwise the whole corridor, the
 * same rule as the per-station PDF.
 *
 * A selection of BINS `[lo, hi]` spans station LINES `[lo, hi + 1]`: the last
 * bin's end station is a section too, and dropping it would silently shorten
 * every export by one drawing.
 */
export function sectionDxfScope<T>(
  lines: readonly T[],
  selection: { fromBin: number; toBin: number } | null,
): { lines: T[]; overCap: boolean } {
  if (lines.length === 0) return { lines: [], overCap: false };
  const lo = selection ? Math.max(0, Math.min(selection.fromBin, selection.toBin)) : 0;
  const hi = selection
    ? Math.min(lines.length - 1, Math.max(selection.fromBin, selection.toBin) + 1)
    : lines.length - 1;
  const picked = lo <= hi ? lines.slice(lo, hi + 1) : [];
  return { lines: picked, overCap: picked.length > MAX_DXF_SECTIONS };
}

export function sectionDxfFilename(alignmentName: string, fromDisplay: number, toDisplay: number): string {
  const slug = alignmentName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'alignment';
  return `${slug}_sections_${Math.round(fromDisplay)}-${Math.round(toDisplay)}.dxf`;
}
